import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { Flex } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import { useAnalytics } from 'src/components/settings/analytics';
import { ConfirmationModal } from 'src/components/settings/modals';
import { useParams } from 'src/router/hooks';

import { useClearMergeQueue } from '../hooks';
import { messages } from '../merge-queues.i18n';

export const MergeQueueClearModal = ({
  onClose,
  focusedMergeQueue,
}: {
  onClose: () => void;
  focusedMergeQueue: MergeQueue | undefined;
}) => {
  const { publishButtonClickEvent } = useAnalytics({
    source: 'mergeQueueClearModal',
  });
  const { formatMessage } = useIntl();

  const { repositoryOwner: owner, repositorySlug: slug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();

  const { clearMergeQueue, isClearingMergeQueue } = useClearMergeQueue({
    mergeQueueUuid: focusedMergeQueue?.uuid || '',
    owner,
    slug,
    onClose,
  });

  const shouldShowWarning = useMemo(
    () => focusedMergeQueue?.isActive,
    [focusedMergeQueue?.isActive]
  );

  const modalBody = useMemo(
    () => (
      <Flex direction="column" gap="space.100">
        {formatMessage(messages.mergeQueueClearModalBody)}
        {shouldShowWarning && (
          <SectionMessage appearance="warning">
            {formatMessage(messages.mergeQueueClearWarningForActiveQueue)}
          </SectionMessage>
        )}
      </Flex>
    ),
    [formatMessage, shouldShowWarning]
  );

  return (
    <ConfirmationModal
      appearance="warning"
      header={formatMessage(messages.mergeQueueClearModalHeader)}
      body={modalBody}
      onConfirm={() => {
        publishButtonClickEvent('confirmClear');
        if (focusedMergeQueue?.uuid) {
          clearMergeQueue(focusedMergeQueue.uuid);
        }
      }}
      onCancel={onClose}
      isDisabled={!focusedMergeQueue?.uuid || shouldShowWarning}
      isLoading={isClearingMergeQueue}
      customButtonText={{
        confirm: formatMessage(messages.mergeQueueClearModalHeader),
        cancel: formatMessage(messages.cancel),
      }}
    />
  );
};
